import React, { useState, useEffect, useMemo, Suspense } from "reactn";

/** External libraries **/
import { Router, Switch, Route, Redirect } from "react-router-dom";
import { ErrorBoundary } from "@appsignal/react";
import UserInactivity from 'react-native-user-inactivity';

/** Modules **/
import History from "../navigator/history";
import PublicRoute from "../navigator/PublicRoute";
import PrivateRoute from "../navigator/PrivateRoute";
import Utils from '../lib/Utils';
import LoadingTemplate from "../templates/LoadingTemplate";
import ErrorTemplate from "../templates/ErrorTemplate";
import JwtErrorTemplate from "../templates/JwtErrorTemplate";
import SettingsProvider from "../providers/SettingsProvider";

/** Lazy load **/
const Login = React.lazy(() => import('./Login'));
const RememberPassword = React.lazy(() => import('./RememberPassword'));
const DashboardTemplate = React.lazy(() => import('../templates/DashboardTemplate'));
const UserSwitch = React.lazy(() => import('./UserSwitch'));

/** Hooks **/
import { useAuth } from '../hooks/useAuth';

/** Constants **/
const MINUTES_TO_MILISECONDS_FACTOR = 60 * 1000

const AppNavigator = (props) => {
  const [showAlertExpiration, setShowAlert] = useState(false)
  const [user, signOut, loading, authError] = useAuth();
  const [inactivityTimeout, setInactivityTimeout] = useState(60 * MINUTES_TO_MILISECONDS_FACTOR);
  const devEnvExtraTime = useMemo(() => (props.environment * 100000000000), [props.environment])

  useEffect(() => {
    if (user) {
      fetchInactivityTimeout()
    }
  }, [user])

  const fetchInactivityTimeout = async () => {
    let result = await SettingsProvider.getAdminAttribute('dashboard_session_time')
    if (result.error) {
      setInactivityTimeout((60 * MINUTES_TO_MILISECONDS_FACTOR) + devEnvExtraTime)
    } else {
      setInactivityTimeout((result.value * MINUTES_TO_MILISECONDS_FACTOR) + devEnvExtraTime)
    }
  }

  const onInactivityTimeout = async (action) => {
    if(!action){
      await signOut();
      setShowAlert(true);
    }
  }

  if (loading) return (<LoadingTemplate/>)

  if (authError) return (<JwtErrorTemplate error={authError}/>)

  return (
    <Router history={History}>
      <Switch>
        <PublicRoute user={user} restricted={true} path={"/login"}>
          <Suspense fallback={<LoadingTemplate/>}>
            <Login
              showAlertExpiration={showAlertExpiration}
              onCloseAlert={() => setShowAlert(false)}/>
          </Suspense>
        </PublicRoute>
        <PublicRoute user={user} restricted={true} path={"/remember_password"}>
          <Suspense fallback={<LoadingTemplate/>}>
            <RememberPassword/>
          </Suspense>
        </PublicRoute>
        <PrivateRoute user={user} path={'/*'}>
          <UserInactivity
            timeForInactivity={inactivityTimeout}
            onAction={onInactivityTimeout}>
            <ErrorBoundary
              instance={props.appsignal}
              fallback={(error) => <ErrorTemplate error={error} />}>
              <Suspense fallback={<LoadingTemplate/>}>
                <DashboardTemplate>
                  <UserSwitch/>
                </DashboardTemplate>
              </Suspense>
            </ErrorBoundary>
          </UserInactivity>
        </PrivateRoute>
      </Switch>
    </Router>
  )
}

export default AppNavigator;
